(ns app.front.view.business.search
  "UIs for searching businesses."
  (:require [app.domain.business.model :as business-model]
            [app.front.ui :as ui]
            [app.front.util :as futil :refer [for-frag]]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-event-db ::init
  #(dissoc % ::data))

(rf/reg-sub ::data :-> ::data)

(rf/reg-sub ::search-term
  :<- [::data]
  :-> ::search-term)

(rf/reg-sub ::selected-business
  :<- [::data]
  :-> ::selected-business)

(rf/reg-sub ::search-in-progress?
  :<- [::data]
  :-> ::search-in-progress?)

(rf/reg-event-fx ::do-search
  (fn [{:keys [db]} _]
    (let [term (get-in db [::data ::search-term])]
      (when-not (str/blank? term)
        {:comm/command {:command          :business/search
                        :method           :get
                        :data             {:term term}
                        :on-success       [::do-search.success]
                        :finally-dispatch [::do-search.done]}
         :db           (assoc-in db [::data ::search-in-progress?] true)}))))

(rf/reg-event-db ::do-search.success
  (fn [db [_ _ results]]
    (assoc-in db [::data ::search-results] results)))

(rf/reg-event-db ::do-search.done
  #(assoc-in % [::data ::search-in-progress?] false))

(rf/reg-event-fx ::search-text-change
  (fn [{:keys [db]} [_ term]]
    {:dispatch-debounce [{:id      ::do-search
                          :timeout 500
                          :action  :dispatch
                          :event   [::do-search]}]
     :db                (if (str/blank? term)
                          (update db ::data dissoc ::search-term ::search-results ::search-in-progress?)
                          (assoc-in db [::data ::search-term] term))}))

(rf/reg-sub ::search-results-raw
  :<- [::data]
  :-> ::search-results)

(rf/reg-sub ::search-results
  (fn [[_ hide-businesses-sub]]
    [(rf/subscribe [::search-results-raw])
     (if hide-businesses-sub
       (rf/subscribe hide-businesses-sub)
       (r/reaction nil))])
  (fn [[results existing-ids] _]
    (cond->> results
      existing-ids (remove (comp existing-ids :business/unique-id)))))

(rf/reg-event-db ::select-existing-business
  (fn [db [_ business]]
    (-> (assoc-in db [::data ::selected-business] business)
        (p/dissoc-in [::data ::search-results]))))

(defn business-display-name [{:keys [lang tr]} {:business/keys [municipality subdivision id] :as business}]
  [:span
   (str (->> [(business-model/format-name business lang)
              municipality
              (tr [(keyword "subdivision" (name subdivision))])]
             (remove nil?)
             (str/join ", "))
        (when id
          (str " (" (tr [:business/id]) ": " id ")")))])

(defn authority-existing-business-search
  "Component for searching existing businesses. `target-id` is optional and used only when searching under party.

  `hide-businesses-sub` can take a subscription that returns a set of business ids to hide from the search results.
  `on-select' is an optional callback"
  [{:keys [tr lang on-select hide-businesses-sub light]}]
  [:div.relative.w-full
   [ui/search (p/assoc-if
                {:id                      "party-business-search"
                 :class                   "h-4"
                 ;; search results for a dropdown, autocomplete interferes with that
                 :auto-complete           "off"
                 :label-text              (tr [:party-modal/business-search-placeholder]
                                              [(tr [:business/id])])
                 :placeholder             (tr [:party-modal/business-search-placeholder]
                                              [(tr [:business/id])])
                 :close-button-label-text (tr [:search/empty])
                 :value                   (<sub [::search-term])
                 :on-change               #(rf/dispatch-sync [::search-text-change (-> % futil/tval)])
                 :search-in-progress?     (<sub [::search-in-progress?])
                 :loading-style           {:top "3px"}
                 :loading-text            (tr [:search/loading])
                 :clear-fn                #(>evt [::search-text-change ""])}
                :light light)]
   (when-let [results (<sub [::search-results hide-businesses-sub])]
     [:div.absolute.z-50.w-full.bg-gray-200.shadow-lg
      [:ul
       (for-frag [business results]
         [:li.p-2.hover:bg-gray-300.cursor-pointer
          {:role     "button"
           :on-click (or (when on-select #(on-select business)) #(>evt [::select-existing-business business]))}
          [business-display-name (util/mmap lang tr) business]])]])])

(defn selected-business-display [{:keys [lang tr]}]
  (when-let [selected-business (<sub [::selected-business])]
    [:div.bg-ui-blue-10.p-4.rounded-md.mt-3.bold.flex.items-center
     [business-display-name (util/mmap lang tr) selected-business]
     (cc/button {:kind             :ghost
                 :size             :small
                 :icon-position    :right
                 :icon             i/remove
                 :icon-description (tr [:cancel])
                 :on-click         #(>evt [::select-existing-business nil])
                 :class-name       "only-icon-button ml-2"}
                "")]))
